import { AlertCircle, History, Loader2, RefreshCw } from "lucide-react";

import { EmptyState, LoadingCards } from "components/states";
import { Badge } from "components/ui/badge";
import { Button } from "components/ui/button";
import { Card } from "components/ui/card";

import { describeReadFailure } from "./describe-read-failure";
import type { AuditLogEntry } from "./schemas";
import { useAuditLog } from "./use-audit-log";
import { APP_LOCALE } from "utils";

export function AuditLogSection() {
  const audit = useAuditLog();

  if (audit.status === "loading") return <LoadingCards cards={4} />;
  if (audit.status === "error")
    return (
      <EmptyState icon={AlertCircle} title="Could not load the audit log">
        {describeReadFailure(audit.error)}
      </EmptyState>
    );

  return (
    <div className="space-y-4" data-testid="audit-content">
      <div className="flex items-center justify-between">
        <Badge variant="outline">{audit.data.length} entries</Badge>
        <Button
          variant="outline"
          size="sm"
          onClick={audit.reload}
          disabled={audit.loadingMore}
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {audit.data.length === 0 ? (
        <EmptyState icon={History} title="No audit entries">
          Completed governance commands will appear here.
        </EmptyState>
      ) : (
        <>
          <div className="space-y-2">
            {audit.data.map((entry) => (
              <AuditLogCard key={entry.id} entry={entry} />
            ))}
          </div>
          {audit.hasMore && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={audit.loadMore}
                disabled={audit.loadingMore}
              >
                {audit.loadingMore && (
                  <Loader2 className="h-4 w-4 animate-spin" />
                )}
                Load older entries
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function AuditLogCard({ entry }: { entry: AuditLogEntry }) {
  const at = entry.at.toDate();
  return (
    <Card className="p-4">
      <div className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="font-mono text-sm font-semibold text-foreground wrap-anywhere">
              {entry.action}
            </p>
            <p className="mt-1 font-mono text-xs text-muted-foreground wrap-anywhere">
              {entry.targetRef}
            </p>
          </div>
          <time
            dateTime={at.toISOString()}
            className="text-xs text-muted-foreground"
            data-testid={`audit-time-${entry.id}`}
          >
            {at.toLocaleString(APP_LOCALE)}
          </time>
        </div>
        <p className="text-xs text-muted-foreground">
          Actor: <span className="font-mono">{entry.actorUid}</span>
        </p>
        {(entry.before || entry.after) && (
          <details className="text-sm">
            <summary className="inline-flex cursor-pointer items-center font-medium text-lit max-sm:min-h-11">
              View changes
            </summary>
            <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-2">
              {entry.before && (
                <AuditValue label="Before" value={entry.before} />
              )}
              {entry.after && <AuditValue label="After" value={entry.after} />}
            </div>
          </details>
        )}
      </div>
    </Card>
  );
}

function AuditValue({
  label,
  value,
}: {
  label: string;
  value: Record<string, unknown>;
}) {
  // min-w-0, or the grid track grows to the longest line of the JSON and
  // the page scrolls sideways; the <pre> scrolls inside itself instead.
  return (
    <div className="min-w-0">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
      <pre className="overflow-x-auto rounded-lg border border-border bg-muted p-3 text-xs text-foreground">
        {JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
